import { useCallback, useMemo, useState } from "react";

import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { getAllWarehouseOption } from "Components/WarehouseSelect/util";
import { useAppState } from "../../../../../../context/StateContext";
import { Error } from "Models/error";
import { Filters, Searches, Sorts } from "Models/shipeezi.table";
import { Transshipment } from "Models/transshipment";
import TransshipmentService from "Services/transshipment.service";
import UserService from "Services/user.service";
import { checkError } from "Util/checkError";
import { Status, TaskStatus, UserTypes } from "Util/enums";
import { handlesGrid } from "Util/handlesGrid";
import { UserKeys } from "Util/queries";
import { formatItemJobTo, formatItemLocationFrom } from "Util/formatItemLocation";
import { useQueryClient } from "@tanstack/react-query";

export interface IUpdateAssigneeLeader {
  leaderId: number | null;
  transshipmentId: number;
}

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 5 * 60 * 60 * 1000; // 5 hours

export const useTransshipmentActions = () => {
  const { currentUser, warehouse, warehouseList } = useAppState();
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();

  const allWarehouseOption = getAllWarehouseOption();

  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);

  const [dateTo, setDateTo] = useState<string>();
  const [dateFrom, setDateFrom] = useState<string>();
  const [selectedTransshipment, setSelectedTransshipment] = useState<Transshipment>();
  const { handleCurrentPage, handleFilter, handlePageSize, handleSort, handleSearch } = handlesGrid(
    {
      setFilters,
      setSorting,
      setPage,
      setPageSize,
      setSearches,
    }
  );

  const getWarehouseId = useCallback(() => {
    if (warehouse && warehouseList?.length && warehouse.id === allWarehouseOption.id) {
      return warehouseList?.map((warehouse) => warehouse.id) as number[];
    }

    return warehouse?.id;
  }, [warehouseList, warehouse?.id, allWarehouseOption.id]);

  const handleSortTransshipmentToAndFrom = useCallback((sorts: Sorts) => {
    if (sorts && sorts.length) {
      const sortTransshipmentLocationsIndex = sorts.findIndex((s) =>
        s.includes("transshipmentLocations")
      );

      if (
        sortTransshipmentLocationsIndex !== -1 &&
        !sorts[sortTransshipmentLocationsIndex].includes("number")
      ) {
        sorts[sortTransshipmentLocationsIndex].splice(-1, 0, "number");
      }
    }

    return sorts;
  }, []);

  const attributes = useMemo(
    () => ({
      createdBy: ["id", "fullName"],
      leader: ["id", "fullName"],
      type: ["id", "name"],
      warehouse: ["id", "name"],
      commentPicture: ["id", "mediaLink"],
      jobCustomer: ["fullName"],
      jobBranchFrom: ["name"],
      jobToBranch: ["name"],
      itemJobRoom: ["id", "name"],
      itemJobItemType: ["id", "name"],
      itemJobSymbol: ["id", "name"],
      task: ["status", "id"],
    }),
    []
  );

  const invalidateTransshipmentsCache = () => {
    queryClient.invalidateQueries({
      queryKey: ["transshipments.list"],
    });
    queryClient.invalidateQueries({
      queryKey: ["transshipments.count"],
    });
  };

  const transshipmentQueries = useQueries({
    queries: [
      {
        queryKey: [
          "transshipments.list",
          currentUser?.companyId,
          page,
          warehouse?.id,
          pageSize,
          JSON.stringify(filters),
          JSON.stringify(searches),
          JSON.stringify(sorting),
          dateTo,
          dateFrom,
        ],
        queryFn: () =>
          TransshipmentService.getAll({
            page,
            pageSize,
            companyId: currentUser?.companyId,
            warehouseId: getWarehouseId(),
            date_from: dateFrom,
            date_to: dateTo,
            sortBy:
              sorting && sorting?.length > 0
                ? JSON.stringify([...handleSortTransshipmentToAndFrom(sorting), ["dueBy", "desc"]])
                : JSON.stringify([["dueBy", "desc"]]),
            include: JSON.stringify([
              { association: "type", attributes: attributes.type },
              { association: "createdBy", attributes: attributes.createdBy },
              {
                association: "task",
                attributes: attributes.task,
                include: [
                  {
                    association: "jobs",
                    attributes: ["sequence", "reference"],
                    include: [
                      { association: "customer", attributes: ["fullName", "name", "lastName"] },
                    ],
                  },
                ],
              },
              {
                association: "transshipmentLocations",
                attributes: ["id", "fromStorageBoxName"],
                include: [
                  {
                    association: "fromStorageBox",
                    attributes: ["id", "storageBoxTypeId", "number"],
                    include: [{ association: "storageBoxType" }],
                  },
                  {
                    association: "toStorageBox",
                    attributes: ["id", "storageBoxTypeId", "number"],
                    include: [{ association: "storageBoxType" }],
                  },
                  {
                    association: "fromMapCell",
                    include: [
                      {
                        association: "map",
                        attributes: ["id"],
                        include: [
                          {
                            association: "section",
                            attributes: ["name"],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    association: "toMapCell",
                    include: [
                      {
                        association: "map",
                        attributes: ["id"],
                        include: [
                          {
                            association: "section",
                            attributes: ["name"],
                          },
                        ],
                      },
                    ],
                  },
                  //{
                  //  association: "itemJob",
                  //  include: [
                  //    {
                  //      association: "job",
                  //      include: [
                  //        {
                  //          association: "customer",
                  //          attributes: attributes.jobCustomer,
                  //        },
                  //        {
                  //          association: "branchFrom",
                  //          attributes: attributes.jobBranchFrom,
                  //        },
                  //        {
                  //          association: "toBranch",
                  //          attributes: attributes.jobToBranch,
                  //        },
                  //      ],
                  //    },
                  //    { association: "itemType", attributes: attributes.itemJobItemType },
                  //    { association: "storageBoxes" },
                  //    { association: "room", attributes: attributes.itemJobRoom },
                  //    { association: "symbol", attributes: attributes.itemJobSymbol },
                  //    {
                  //      association: "exceptions",
                  //      include: [
                  //        { association: "exceptionSymbol" },
                  //        { association: "locationSymbol" },
                  //      ],
                  //    },
                  //  ],
                  //},
                ],
              },
              { association: "leader", attributes: attributes.leader },
              //{ association: "warehouse", attributes: attributes.warehouse },
              //{
              //  association: "comments",
              //  include: [
              //    {
              //      association: "createdBy",
              //      include: [{ association: "picture", attributes: attributes.commentPicture }],
              //    },
              //  ],
              //},
            ]),
            searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
            ...filters,
          }),
        enabled: !!currentUser?.companyId && !!warehouse?.id,
        select: (response: any) =>
          response.data.map((item: any) => ({
            ...item,
            processedFromLocations: formatItemLocationFrom(item.transshipmentLocations),
            processedToLocations: formatItemJobTo(item.transshipmentLocations),
          })),
      },
      // Second query - count
      {
        queryKey: [
          "transshipments.count",
          currentUser?.companyId,
          JSON.stringify(filters),
          JSON.stringify(searches),
          warehouse,
          dateFrom,
          dateTo,
        ],
        queryFn: () =>
          TransshipmentService.count({
            date_from: dateFrom,
            date_to: dateTo,
            companyId: currentUser?.companyId,
            warehouseId: getWarehouseId(),
            searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
            ...filters,
          }),
        enabled: !!currentUser?.companyId && !!warehouse?.id,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        keepPreviousData: true,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        onSuccess: (response: any) => {
          setTotal(response?.data || 0);
        },
      },
    ],
  });

  // Extract the individual query results
  const transShipmentsQuery = transshipmentQueries[0];
  const countQuery = transshipmentQueries[1];
  // For backward compatibility with existing code
  const getTransshipments = () => transShipmentsQuery;
  const getTransshipmentsCount = () => countQuery;

  const refetchTransshipments = transShipmentsQuery?.refetch;

  const usePublishTransshipment = useCallback(
    () =>
      useMutation(
        ["transshipment.send"],
        ({ transshipment }: { transshipment: Transshipment }) =>
          TransshipmentService.update(Number(transshipment?.id), {
            ...transshipment,
            status: TaskStatus.PUBLISHED,
          }),
        {
          onSuccess: () => {
            NotificationToast.success(translate("operationPerformedSuccessfully"), {
              id: "request.notification.success.transshipment.send",
            });
            refetchTransshipments();
          },
          onError: (error: AxiosError<Error>) => {
            checkError(error, translate("anErrorOccurredTryAgain"));
          },
        }
      ),
    [refetchTransshipments]
  );

  const useUpdateTransshipmentStatus = useCallback(
    (callbackSuccess?: () => void, callbackFail?: () => void) =>
      useMutation(
        ["transshipment.update.status"],
        ({ transshipment }: { transshipment: Pick<Transshipment, "status" | "id"> }) =>
          TransshipmentService.updateStatus(transshipment, Number(transshipment?.id)),
        {
          onSuccess: () => {
            NotificationToast.success(translate("operationPerformedSuccessfully"), {
              id: "request.notification.success.transshipment.update.status",
            });
            refetchTransshipments();
            if (callbackSuccess) callbackSuccess();
          },
          onError: (error: AxiosError<Error>) => {
            checkError(error, translate("anErrorOccurredTryAgain"));
            if (callbackFail) callbackFail();
          },
        }
      ),
    [refetchTransshipments]
  );

  const {
    mutate: updateTransshipmentAssigneeLeader,
    isLoading: isUpdatingTransshipmentAssigneeLeader,
  } = useMutation(
    ["update.transshipmet.leader"],
    async ({ leaderId, transshipmentId }: IUpdateAssigneeLeader) => {
      const response = await TransshipmentService.updateAssigneeLeader({
        leaderId,
        transshipmentId,
      });

      return response;
    },
    {
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
      onSuccess: (response: any) => {
        if (response && response.data)
          NotificationToast.success(translate("theAssignedPersonHasBeenChangedSuccessfully"));
      },
    }
  );

  const updateTransshipmentList = (
    leaderId: number | null,
    transshipmentId: number,
    callback: () => void
  ) => {
    return updateTransshipmentAssigneeLeader(
      { leaderId: leaderId || null, transshipmentId },
      {
        onSuccess: () => {
          callback();
        },
        onError: () => callback(),
      }
    );
  };

  const getTransshipmentsAssigneeLeader = () => {
    const includeUserCompanies = {
      association: "usersCompanies",
      required: true,
      where: {
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        typeUser: [UserTypes.ADMIN, UserTypes.MOVING_WAREHOUSE, UserTypes.OPERATIONS],
      },
    };

    const warehouseBranchesId = warehouse?.branches?.map((branch) => branch.id);

    const includeBranches = {
      association: "branches",
      required: true,
      where: {
        id: warehouseBranchesId,
      },
    };

    return useQuery({
      queryKey: UserKeys.getAll([currentUser?.companyId, warehouseBranchesId]),
      queryFn: () =>
        UserService.getAll({
          include: JSON.stringify([includeUserCompanies, includeBranches]),
          sortBy: JSON.stringify([["name", "asc"]]),
        }),
      select: (response: any) => {
        if (response && response?.data?.length > 0) return response.data;

        return [];
      },
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      enabled: !!currentUser?.companyId && !!warehouse?.id,
    });
  };

  return {
    total,
    getTransshipments,
    getTransshipmentsCount,
    pageSize,
    page,
    dateTo,
    dateFrom,
    setDateTo,
    setDateFrom,
    handleCurrentPage,
    handleFilter,
    handlePageSize,
    handleSort,
    handleSearch,
    setSorting,
    usePublishTransshipment,
    useUpdateTransshipmentStatus,
    updateTransshipmentList,
    isUpdatingTransshipmentAssigneeLeader,
    getTransshipmentsAssigneeLeader,
    refetchTransshipments,
    selectedTransshipment,
    setSelectedTransshipment,
    invalidateTransshipmentsCache,
  };
};
